'use client';
import { useContext, useEffect, useRef, useState } from 'react';
import { Turnstile, type TurnstileInstance } from '@marsidev/react-turnstile';
import { FormContext } from './index';

/** If the widget hasn't loaded by then, its script was almost certainly blocked
 * (corporate filter, privacy extension) and no callback will ever fire. */
export const TURNSTILE_LOAD_TIMEOUT_MS = 15_000;

/** Ids of the prompts below, so the waiting submit button can point at the reason. */
export const TURNSTILE_PROMPT_ID = 'turnstile-prompt';
export const TURNSTILE_ERROR_ID = 'turnstile-error-message';

interface TurnstileFieldProps {
  className?: string;
}

export function TurnstileField({ className = '' }: TurnstileFieldProps) {
  const siteKey = process.env.NEXT_PUBLIC_TURNSTILE_SITE_KEY;
  // Report the widget's state to the enclosing ContactForm so Submit waits for a token —
  // a tokenless submit is rejected server-side (?error=verification-required).
  const { turnstileStatus, setTurnstileStatus, registerTurnstileReset } = useContext(FormContext);
  const widget = useRef<TurnstileInstance>(null);
  // Bumped to re-create the widget when there is no instance to reset.
  const [widgetKey, setWidgetKey] = useState(0);
  const loadWatchdog = useRef<ReturnType<typeof setTimeout> | null>(null);
  const clearWatchdog = () => {
    if (loadWatchdog.current) clearTimeout(loadWatchdog.current);
    loadWatchdog.current = null;
  };
  // No callback at all (blocked script, a reset that never settles) would leave Submit
  // waiting with no explanation; after the timeout, say so.
  const armWatchdog = (cause: string) => {
    clearWatchdog();
    loadWatchdog.current = setTimeout(() => {
      console.error(`[Turnstile] ❌ No token (${cause})`);
      setTurnstileStatus('error', cause);
    }, TURNSTILE_LOAD_TIMEOUT_MS);
  };

  useEffect(() => {
    if (!siteKey) return;
    setTurnstileStatus('pending');
    armWatchdog('load-timeout');
    return () => {
      clearWatchdog();
      setTurnstileStatus('absent');
    };
    // armWatchdog only touches refs and the stable setter.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [siteKey, setTurnstileStatus]);

  // A rejected in-page submit spent the single-use token on the server's check (the page
  // no longer reloads to get a new one). ContactForm calls this straight away: Submit
  // waits again, the widget re-runs its check as on first load (re-created if there is no
  // instance to reset), and the hidden token field is cleared by the reset.
  useEffect(() => {
    if (!siteKey || !registerTurnstileReset) return;
    registerTurnstileReset(() => {
      setTurnstileStatus('pending');
      armWatchdog('reset-timeout');
      if (widget.current) widget.current.reset();
      else setWidgetKey(key => key + 1);
    });
    return () => registerTurnstileReset(null);
    // armWatchdog only touches refs and the stable setter.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [siteKey, registerTurnstileReset, setTurnstileStatus]);

  if (!siteKey) {
    console.error('[Turnstile] ❌ NEXT_PUBLIC_TURNSTILE_SITE_KEY is not configured');
    return null;
  }

  const handleSuccess = (token: string) => {
    console.log('[Turnstile] ✅ Token received:', token.substring(0, 20) + '...');
    clearWatchdog();
    setTurnstileStatus('ready');
  };

  // Blocked by an extension/network, unsupported browser, or a hostname the site key
  // doesn't allow (e.g. preview deploys): no token will ever arrive.
  // `error` is a Cloudflare error code (e.g. 110200 = hostname not allowed) or our own
  // cause; it reaches Sentry via ContactForm.
  const handleError = (error: string) => {
    console.error('[Turnstile] ❌ Error:', error);
    clearWatchdog();
    setTurnstileStatus('error', error);
  };

  // Rendered — also undoes a watchdog error if the script was just slow, not blocked.
  // Safe: render returns before any token callback can fire.
  const handleWidgetLoad = () => {
    clearWatchdog();
    setTurnstileStatus('pending');
  };

  // Cloudflare wants a click ("Verify you are human"), e.g. on a re-check after a rejected
  // submit. Not a stuck "Verifying…": the field asks for the click and Submit waits.
  const handleBeforeInteractive = () => {
    clearWatchdog();
    setTurnstileStatus('interactive');
  };

  // The widget refreshes an expired token itself (refreshExpired: 'auto'), so wait again.
  const handleExpire = () => {
    console.warn('[Turnstile] ⚠️  Token expired');
    setTurnstileStatus('pending');
  };

  return (
    <div className={className}>
      <Turnstile
        key={widgetKey}
        ref={widget}
        siteKey={siteKey}
        options={{
          theme: 'dark',
          size: 'normal',
          appearance: 'interaction-only',
        }}
        // Side effect: the library drops its global onload callback, so api.js logs a harmless
        // "Unable to find onload callback" warning and the library falls back to polling.
        scriptOptions={{ onError: () => handleError('script-blocked') }}
        onWidgetLoad={handleWidgetLoad}
        onBeforeInteractive={handleBeforeInteractive}
        onSuccess={handleSuccess}
        onError={handleError}
        onExpire={handleExpire}
        // Interactive challenge not solved in time: the widget shows a fresh one itself
        // (refresh-timeout: auto), so keep asking rather than alarm the user.
        onTimeout={() => setTurnstileStatus('interactive')}
        onUnsupported={() => handleError('unsupported')}
      />
      {turnstileStatus === 'interactive' && (
        <p id={TURNSTILE_PROMPT_ID} data-testid="turnstile-interactive" className="mt-2 text-sm text-gray-4">
          Please complete the security check above, then send the form.
        </p>
      )}
      {turnstileStatus === 'error' && (
        <p id={TURNSTILE_ERROR_ID} data-testid="turnstile-error" className="mt-2 text-sm text-core-error" role="alert">
          We couldn&apos;t run our security check, so this form can&apos;t be sent. Try turning off ad or tracker
          blockers for this page and reloading, or email{' '}
          <a href="mailto:info@gradle.com" className="underline">
            info@gradle.com
          </a>
          .
        </p>
      )}
    </div>
  );
}
